import { useEffect, useState } from 'react';
import { message } from 'antd';
import {
  marketingService,
  Campaign,
  CreateCampaignRequest,
  UpdateCampaignRequest,
} from '../../api/services/marketingService';

export const useInfluencerLinks = () => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editing, setEditing] = useState<Campaign | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const result = await marketingService.getCampaigns();
      setCampaigns(Array.isArray(result) ? result : []);
    } catch {
      message.error('Failed to load influencer links');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (values: CreateCampaignRequest) => {
    setSubmitting(true);
    try {
      await marketingService.createCampaign(values);
      message.success('Influencer link created');
      setModalVisible(false);
      fetchCampaigns();
    } catch (e) {
      message.error(
        (e as { message?: string })?.message || 'Failed to create link',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (id: string, values: UpdateCampaignRequest) => {
    setSubmitting(true);
    try {
      await marketingService.updateCampaign(id, values);
      message.success('Influencer link updated');
      setModalVisible(false);
      fetchCampaigns();
    } catch (e) {
      message.error(
        (e as { message?: string })?.message || 'Failed to update link',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await marketingService.deleteCampaign(id);
      message.success('Influencer link deleted');
      fetchCampaigns();
    } catch {
      message.error('Failed to delete link');
    }
  };

  const openCreateModal = () => {
    setEditing(null);
    setModalVisible(true);
  };

  const openEditModal = (campaign: Campaign) => {
    setEditing(campaign);
    setModalVisible(true);
  };

  useEffect(() => {
    fetchCampaigns();
  }, []);

  return {
    campaigns,
    loading,
    modalVisible,
    editing,
    submitting,
    setModalVisible,
    handleCreate,
    handleUpdate,
    handleDelete,
    openCreateModal,
    openEditModal,
    refetch: fetchCampaigns,
  };
};
